import { Box, Collapse, FormControlLabel, Switch, Typography } from '@mui/material';
import { marked } from 'marked';
import React from 'react';
import { Markdown } from './Markdown';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const Prism = require('prismjs');
require('prismjs/themes/prism-okaidia.css');
require('prismjs/components/prism-jsx');
require('prismjs/components/prism-typescript');
require('prismjs/components/prism-tsx');

export interface ExampleProps {
    name: string;
    doc?: string;
    import: { default: React.FC; src: string };
    componentHiddenInitially?: boolean;
}

export const Example: React.FC<ExampleProps & { component: string }> = React.memo((props) => {
    const [showComponent, setShowComponent] = React.useState(!props.componentHiddenInitially);
    const [showSource, setShowSource] = React.useState(false);
    const html = props.doc && marked(props.doc);
    const Component = props.import.default;
    const source = props.import.src
        .replace(/from '\.\/index'/g, `from '${process.env.PACKAGE_NAME}/${props.component}'`)
        .replace(/from '\.\.\/(.*)'/g, `from '${process.env.PACKAGE_NAME}/$1'`);
    const sourceHighlighted = Prism.highlight(source, Prism.languages.tsx, 'tsx');
    return (
        <Box sx={{ marginBottom: 2 }}>
            <Typography sx={{ marginBottom: 2 }} variant='h5'>
                {props.name}
            </Typography>
            {html && <Markdown html={html} />}
            {props.componentHiddenInitially && (
                <FormControlLabel
                    control={
                        <Switch checked={showComponent} onChange={(event) => setShowComponent(event.target.checked)} color='primary' />
                    }
                    label='Show example'
                />
            )}
            {showComponent && (
                <Box sx={{ padding: 2, border: (theme) => '1px solid ' + theme.palette.divider, borderRadius: 1 }}>
                    <Component />
                </Box>
            )}
            <Box sx={{ paddingTop: 1 }}>
                <FormControlLabel
                    control={<Switch checked={showSource} onChange={(event) => setShowSource(event.target.checked)} color='primary' />}
                    label='Show source code'
                />
                <Collapse in={showSource}>
                    <Box
                        component={'pre'}
                        className={'example-src language-tsx'}
                        sx={{
                            margin: '0 !important',
                            marginTop: (theme) => theme.spacing(1) + 'px !important',
                            background: '#2a3339 !important',
                            borderRadius: (theme) => theme.spacing(1) + 'px !important',
                        }}
                    >
                        <Box
                            component={'code'}
                            sx={{ fontFamily: "'Consolas', 'Liberation Mono', 'Menlo', 'Courier', monospace", fontSize: '0.9rem' }}
                            dangerouslySetInnerHTML={{ __html: sourceHighlighted }}
                        />
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
});
